import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadState, UserRoles } from '@diditusa/shared/types/commonTypes';

// Reusable button for route navigation
const NavButton = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const navigate = useNavigate();
  return (
    <Button
      onClick={() => navigate(to)}
      sx={{ color: '#fff', fontWeight: 800, fontFamily: "'Nunito', sans-serif" }}
    >
      {children}
    </Button>
  );
};

function defaultNavBar() {
  return (
    <>
      <NavButton to='/home'>Home</NavButton>
      <NavButton to='/how'>How it works</NavButton>
      <NavButton to='/previousOrders'>Orders</NavButton>
      <NavButton to='/about'>About Us</NavButton>
      <NavButton to='/new-order/1/Essential Protection'>
        <Button
          variant='contained'
          style={{ fontFamily: "'Nunito', sans-serif", fontWeight: '800' }}
        >
          Book now
        </Button>
      </NavButton>
    </>
  );
}

function employeeNavBar() {
  return (
    <>
      <NavButton to='/home'>Home</NavButton>
      <NavButton to='/agent/jobs'>My jobs</NavButton>
      <NavButton to='/orders'>Orders</NavButton>
      <Button variant='contained'>Book now</Button>
    </>
  );
}

function managerNavBar() {
  return (
    <>
      <NavButton to='/home'>Home</NavButton>
      <NavButton to='/company/jobs'>Jobs</NavButton>
      <NavButton to='/company/agents'>Agents</NavButton>
    </>
  );
}

const NavBar: React.FC = () => {
  const { isAuthenticated, user, logout, login, userRole, loadState } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

  const renderLinks = () => {
    const items = !isAuthenticated
      ? ['About Us', 'Contact', 'How it Works']
      : userRole === UserRoles.MANAGER
        ? ['Home', 'Jobs', 'Agents']
        : userRole === UserRoles.EMPLOYEE
          ? ['Home', 'My jobs', 'Orders']
          : ['Home', 'Orders', 'How it Works', 'About Us'];

    return items.map((text) => (
      <ListItemButton key={text} onClick={() => navigate(getPath(text))}>
        <ListItemText primary={text} />
      </ListItemButton>
    ));
  };

  const getPath = (label: string): string => {
    switch (label) {
      case 'Home':
        return '/home';
      case 'Orders':
        return '/previousOrders';
      case 'My jobs':
        return '/agent/jobs';
      case 'Jobs':
        return '/company/jobs';
      case 'Agents':
        return '/company/agents';
      case 'About Us':
        return '/about';
      case 'Contact':
        return '/contact';
      case 'How it Works':
        return '/how';
      default:
        return '/';
    }
  };

  return (
    <>
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          bgcolor: 'black',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottom: '1px solid rgb(0, 0, 0)',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: isMobile ? 2 : 10,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: isMobile ? 'center' : 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='h4'
              fontWeight={700}
              color='#fff'
              fontFamily="'Nunito', sans-serif"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/home')}
            >
              SAëF
            </Typography>
          </Box>

          {/* Navigation or Burger */}
          {isMobile ? (
            <IconButton edge='end' color='inherit' onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
          ) : (
            <Box display='flex' alignItems='center' gap={4}>
              {loadState === LoadState.LOADED || loadState === LoadState.ERROR ? (
                !isAuthenticated ? (
                  <>
                    {['About Us', 'Contact'].map((text) => (
                      <Button key={text} sx={{ color: '#fff', fontWeight: 500 }}>
                        {text}
                      </Button>
                    ))}
                    <NavButton to='/how'>How it works</NavButton>
                    <NavButton to='/about'>About Us</NavButton>
                    <Button
                      onClick={() => login()}
                      variant='outlined'
                      sx={{ bgcolor: '#343a40', color: '#fff' }}
                    >
                      Log In
                    </Button>
                  </>
                ) : (
                  <>
                    {userRole === UserRoles.MANAGER
                      ? managerNavBar()
                      : userRole === UserRoles.EMPLOYEE
                        ? employeeNavBar()
                        : defaultNavBar()}
                    <IconButton onClick={handleMenu} size='small' sx={{ ml: 2 }}>
                      <Avatar alt={user?.firstName} src={user?.imageUrl} />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem component={Link} to='/account' onClick={handleClose}>
                        Account
                      </MenuItem>
                      {user?.firstName === 'ADMIN' && (
                        <MenuItem component={Link} to='/admin' onClick={handleClose}>
                          Admin Panel
                        </MenuItem>
                      )}
                      <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    </Menu>
                  </>
                )
              ) : null}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor='right' open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 250 }} role='presentation' onClick={toggleDrawer(false)}>
          <List>
            {renderLinks()}
            {isAuthenticated && (
              <>
                <ListItemButton onClick={() => navigate('/account')}>
                  <ListItemText primary='Account' />
                </ListItemButton>
                <ListItemButton onClick={handleLogout}>
                  <ListItemText primary='Logout' />
                </ListItemButton>
              </>
            )}

            {!isAuthenticated && (
              <ListItemButton onClick={login}>
                <ListItemText primary='Log In' />
              </ListItemButton>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;
